/**
 * Normalize QuestionnaireResponse.item for Medplum form hydration.
 * AI-generated notes use a flat linkId list and valueString on choice fields;
 * the form expects nested groups and valueCoding on choices.
 */
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';

export function indexResponseItemsByLinkId(
  items: QuestionnaireResponseItem[] | undefined,
): Map<string, QuestionnaireResponseItem> {
  const map = new Map<string, QuestionnaireResponseItem>();
  const walk = (list: QuestionnaireResponseItem[] | undefined): void => {
    for (const item of list ?? []) {
      if (item.linkId) map.set(item.linkId, item);
      walk(item.item);
    }
  };
  walk(items);
  return map;
}

function normalizeChoiceAnswers(
  answers: QuestionnaireResponseItem['answer'],
  qItem: QuestionnaireItem,
): QuestionnaireResponseItem['answer'] {
  if (!answers?.length) return answers;
  if (qItem.type !== 'choice' && qItem.type !== 'open-choice') return answers;
  return answers.map((answer) => {
    if (answer.valueCoding) return answer;
    const str = answer.valueString?.trim();
    if (!str) return answer;
    const lower = str.toLowerCase();
    const option = qItem.answerOption?.find((opt) => {
      const coding = opt.valueCoding;
      if (coding?.display?.toLowerCase() === lower) return true;
      if (coding?.code?.toLowerCase() === lower) return true;
      if (opt.valueString?.toLowerCase() === lower) return true;
      return false;
    });
    if (option?.valueCoding) return { valueCoding: option.valueCoding };
    return answer;
  });
}

function buildNestedResponseItems(
  questionnaireItems: QuestionnaireItem[] | undefined,
  byLinkId: Map<string, QuestionnaireResponseItem>,
): QuestionnaireResponseItem[] | undefined {
  const built: QuestionnaireResponseItem[] = [];
  for (const qItem of questionnaireItems ?? []) {
    if (!qItem.linkId) continue;
    const existing = byLinkId.get(qItem.linkId);
    const nested = buildNestedResponseItems(qItem.item, byLinkId);
    const isGroup = qItem.type === 'group' || (nested?.length ?? 0) > 0;

    if (isGroup) {
      // FHIR qrs-1: an item with nested `item` must not also have `answer`.
      const node: QuestionnaireResponseItem = {
        linkId: qItem.linkId,
        text: qItem.text ?? existing?.text,
      };
      if (nested?.length) node.item = nested;
      built.push(node);
      continue;
    }

    if (existing?.answer?.length) {
      built.push({
        linkId: qItem.linkId,
        text: qItem.text ?? existing.text,
        answer: normalizeChoiceAnswers(existing.answer, qItem),
      });
    }
  }
  return built.length ? built : undefined;
}

export function shapeResponseItemsForQuestionnaire(
  items: QuestionnaireResponseItem[] | undefined,
  questionnaire: Questionnaire,
): QuestionnaireResponseItem[] | undefined {
  if (!items?.length) return items;
  const byLinkId = indexResponseItemsByLinkId(items);
  return sanitizeResponseItemsForFhir(buildNestedResponseItems(questionnaire.item, byLinkId) ?? items);
}

/** Drop client-generated item ids before persisting — Medplum matches answers by structure/linkId. */
export function stripQuestionnaireResponseItemIds(
  items: QuestionnaireResponseItem[] | undefined,
): QuestionnaireResponseItem[] | undefined {
  if (!items?.length) return items;
  return items.map(({ id: _id, item, ...rest }) => ({
    ...rest,
    ...(item?.length ? { item: stripQuestionnaireResponseItemIds(item) } : {}),
  }));
}

function indexServerItemIdsByLinkId(
  items: QuestionnaireResponseItem[] | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  const walk = (list: QuestionnaireResponseItem[] | undefined): void => {
    for (const item of list ?? []) {
      if (item.linkId && item.id) map.set(item.linkId, item.id);
      walk(item.item);
    }
  };
  walk(items);
  return map;
}

function applyServerItemIdsByLinkId(
  items: QuestionnaireResponseItem[] | undefined,
  serverIdsByLinkId: Map<string, string>,
): QuestionnaireResponseItem[] | undefined {
  if (!items?.length) return items;
  return items.map((item) => {
    const serverId = item.linkId ? serverIdsByLinkId.get(item.linkId) : undefined;
    const nested = item.item?.length
      ? applyServerItemIdsByLinkId(item.item, serverIdsByLinkId)
      : undefined;
    return {
      ...item,
      ...(serverId ? { id: serverId } : {}),
      ...(nested?.length ? { item: nested } : {}),
    };
  });
}

/**
 * Rebuild response items from the questionnaire tree + form edits (indexed by linkId).
 * When existing items are provided, reuse server item ids by linkId to avoid id-only churn.
 */
export function buildQuestionnaireResponseItemsForSave(
  questionnaire: Questionnaire,
  formItems: QuestionnaireResponseItem[] | undefined,
  existingItems?: QuestionnaireResponseItem[] | undefined,
): QuestionnaireResponseItem[] | undefined {
  if (!formItems?.length) return formItems;
  const byLinkId = indexResponseItemsByLinkId(formItems);
  const nested = buildNestedResponseItems(questionnaire.item, byLinkId) ?? formItems;
  const sanitized = sanitizeResponseItemsForFhir(nested);
  if (existingItems?.length) {
    const serverIds = indexServerItemIdsByLinkId(existingItems);
    return applyServerItemIdsByLinkId(sanitized, serverIds);
  }
  return stripQuestionnaireResponseItemIds(sanitized);
}

// ---------------------------------------------------------------------------
// Repeat-safe shaping for nested form data (chart documents / treatment plans).
//
// The linkId-map functions above collapse repeated group instances (Map keeps a
// single entry per linkId), which loses data for templates with repeating groups
// such as the diagnosis/goal/objective treatment plan. The functions below treat
// the nested response structure as authoritative: every repeated instance is
// preserved in order, identified by structural position rather than linkId alone.
// ---------------------------------------------------------------------------

function buildNestedItemsPreservingRepeats(
  questionnaireItems: QuestionnaireItem[] | undefined,
  responseItems: QuestionnaireResponseItem[] | undefined,
): QuestionnaireResponseItem[] {
  const built: QuestionnaireResponseItem[] = [];
  for (const qItem of questionnaireItems ?? []) {
    if (!qItem.linkId) continue;
    const instances = (responseItems ?? []).filter((item) => item.linkId === qItem.linkId);
    for (const instance of instances) {
      const nested = buildNestedItemsPreservingRepeats(qItem.item, instance.item);
      if (qItem.type === 'group') {
        // Keep empty group instances: a just-added repeat instance is meaningful.
        const node: QuestionnaireResponseItem = {
          linkId: qItem.linkId,
          text: qItem.text ?? instance.text,
        };
        if (instance.id) node.id = instance.id;
        if (nested.length) node.item = nested;
        built.push(node);
        continue;
      }
      if (nested.length) {
        // Question with nested child answers — FHIR qrs-1 forbids answer + item.
        const node: QuestionnaireResponseItem = {
          linkId: qItem.linkId,
          text: qItem.text ?? instance.text,
          item: nested,
        };
        if (instance.id) node.id = instance.id;
        built.push(node);
        continue;
      }
      if (instance.answer?.length) {
        const node: QuestionnaireResponseItem = {
          linkId: qItem.linkId,
          text: qItem.text ?? instance.text,
          answer: normalizeChoiceAnswers(instance.answer, qItem),
        };
        if (instance.id) node.id = instance.id;
        built.push(node);
      }
    }
  }
  return built;
}

/**
 * Repeat-safe hydration: reorder nested response items to questionnaire order while
 * preserving every repeated group instance and its internal order. Use for chart
 * documents and treatment plans whose saved items are already nested.
 */
export function shapeNestedResponseItemsForQuestionnaire(
  items: QuestionnaireResponseItem[] | undefined,
  questionnaire: Questionnaire,
): QuestionnaireResponseItem[] | undefined {
  if (!items?.length) return items;
  const built = buildNestedItemsPreservingRepeats(questionnaire.item, items);
  return sanitizeResponseItemsForFhir(built.length ? built : items);
}

/** Structural path key: linkId plus 0-based index among same-linkId siblings, per level. */
function indexServerIdsByStructuralPath(
  items: QuestionnaireResponseItem[] | undefined,
  prefix = '',
  map: Map<string, string> = new Map(),
): Map<string, string> {
  const occurrence = new Map<string, number>();
  for (const item of items ?? []) {
    if (!item.linkId) continue;
    const index = occurrence.get(item.linkId) ?? 0;
    occurrence.set(item.linkId, index + 1);
    const path = `${prefix}/${item.linkId}#${index}`;
    if (item.id) map.set(path, item.id);
    indexServerIdsByStructuralPath(item.item, path, map);
  }
  return map;
}

function applyServerIdsByStructuralPath(
  items: QuestionnaireResponseItem[] | undefined,
  serverIds: Map<string, string>,
  prefix = '',
): QuestionnaireResponseItem[] | undefined {
  if (!items?.length) return items;
  const occurrence = new Map<string, number>();
  return items.map((item) => {
    if (!item.linkId) return item;
    const index = occurrence.get(item.linkId) ?? 0;
    occurrence.set(item.linkId, index + 1);
    const path = `${prefix}/${item.linkId}#${index}`;
    const serverId = serverIds.get(path);
    const nested = applyServerIdsByStructuralPath(item.item, serverIds, path);
    const { id: _clientId, ...rest } = item;
    void _clientId;
    return {
      ...rest,
      ...(serverId ? { id: serverId } : {}),
      ...(nested?.length ? { item: nested } : {}),
    };
  });
}

/**
 * Repeat-safe persistence: rebuild nested response items in questionnaire order
 * preserving all repeated instances. Server item ids are reused by structural path
 * (linkId + repeat index) on update; client-generated ids are stripped on create.
 */
export function buildNestedQuestionnaireResponseItemsForSave(
  questionnaire: Questionnaire,
  formItems: QuestionnaireResponseItem[] | undefined,
  existingItems?: QuestionnaireResponseItem[] | undefined,
): QuestionnaireResponseItem[] | undefined {
  if (!formItems?.length) return formItems;
  const built = buildNestedItemsPreservingRepeats(questionnaire.item, formItems);
  const sanitized = sanitizeResponseItemsForFhir(built.length ? built : formItems);
  if (existingItems?.length) {
    const serverIds = indexServerIdsByStructuralPath(existingItems);
    return applyServerIdsByStructuralPath(sanitized, serverIds);
  }
  return stripQuestionnaireResponseItemIds(sanitized);
}

/** Enforce FHIR qrs-1 before create/update (no item may have both `item` and `answer`). */
export function sanitizeResponseItemsForFhir(
  items: QuestionnaireResponseItem[] | undefined,
): QuestionnaireResponseItem[] | undefined {
  if (!items?.length) return items;
  return items.map((item) => {
    const nested = sanitizeResponseItemsForFhir(item.item);
    if (nested?.length) {
      const { answer: _answer, item: _item, ...rest } = item;
      void _answer;
      void _item;
      return { ...rest, item: nested };
    }
    return item;
  });
}
